import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AuthenticationService } from '../../services/authentication.service';
import { Store } from '@ngrx/store';
import { UserState } from '../../storage-ngrx/user/user.reducer';
import * as UserActions from '../../storage-ngrx/user/user.actions';
import { User } from '../../interfaces/user';
import { PageState } from '../../storage-ngrx/page/page.state';
import * as PageActions from '../../storage-ngrx/page/page.actions';
import { debounceTime, filter, map, Observable, of, startWith, Subject, switchMap, take, takeUntil } from 'rxjs';
import { CompanyState } from '../../storage-ngrx/company/company.reducer';
import { loadCompany } from '../../storage-ngrx/company/company.actions';
import { CustomerAutocomplete } from '../../main/main-home/pages/customer/interfaces/customer-autocomplete';
import { SearchAutocompleteService } from '../../services/search-autocomplete.service';
import { NotificationWalleyItem } from '../../interfaces/notification-walley';
import { NotificationWalleyService } from '../../services/notification-walley.service';
import { AblyConnectService } from '../../services/ably-connect.service';
import { AssistantConfigService } from '../../services/assistant-config.service';
import { AssistantStore } from '../../components/assistant/assistant.store';
import { WalleyComponent } from '../../components/walley/walley.component';

@Component({
  selector: 'app-header-home',
  imports: [CommonModule, RouterModule, MatIconModule, ReactiveFormsModule, MatAutocompleteModule,
    MatMenuModule, WalleyComponent],
  templateUrl: './header-home.component.html',
  styleUrl: './header-home.component.scss'
})
export class HeaderHomeComponent implements OnInit, OnDestroy {
  nickname$!: Observable<string | null>;
  heightIcon$!: Observable<string | null>;

  idazienda = 0;
  agendaPermesso = true;
  cssIconaAgenda = '';
  logoIcon = 'assets/images/loghi/logo_w.png';
  activeItem = '';
  private destroy$ = new Subject<void>();

  filterForm = new FormGroup({
    customer: new FormControl<CustomerAutocomplete | null>(null),
  });
  filteredCustomers$!: Observable<CustomerAutocomplete[]>;
  path = '';

  notifications: NotificationWalleyItem[] = [];
  unreadCount = 0;
  notificationsLoading = false;
  notificationsLoadingMore = false;
  notificationsHasMore = false;
  private readonly notificationsPageSize = 10;
  private notifyChannel: string | null = null;
  assistantInHeader = false;

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private searchAutocompleteService: SearchAutocompleteService,
    private store: Store<{ user: UserState, page: PageState, company: CompanyState }>,
    private notificationWalleyService: NotificationWalleyService,
    private ablyConnectService: AblyConnectService,
    private assistantConfig: AssistantConfigService,
    readonly assistantStore: AssistantStore,
  ) {}

  toggleAssistant(): void {
    this.assistantStore.toggleHeaderDock();
  }

  logOut(): void {
    this.authenticationService.logoutServer();
  }

  ngOnInit(): void {
    this.store.dispatch(UserActions.loadUserInfo());
    this.store.dispatch(UserActions.loadUserPermissions());
    this.store.dispatch(UserActions.loadUserCompanyInfo());
    this.store.dispatch(loadCompany());

    this.nickname$ = this.store.select(state => state.user.userInfo).pipe(
      map((val: User | null) => val ? val.nickname : '')
    );
    const companyInfo$ = this.store.select(state => state.user.companyInfo);

    this.heightIcon$ = companyInfo$.pipe(
      map(() => '25')
    );

    this.checkActiveItem(this.router.url);

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd) => {
      this.checkActiveItem(event.urlAfterRedirects);
    });

    this.searchCustomer();
    this.filterForm.get('customer')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe((obj) => {
      if (obj &&
        typeof obj.idregistry === 'number' &&
        typeof obj.denomination === 'string'
      ) {
        this.path = obj.path;
        this.goToCustomer(obj.idregistry);
        setTimeout(() => {
          this.filterForm.get('customer')?.setValue(null, { emitEvent: false });
        }, 100);
      }
    });

    this.loadNotifications();
    this.assistantConfig.getConfig().pipe(takeUntil(this.destroy$)).subscribe((config) => {
      this.assistantInHeader = config.visible === 1 && config.header_hidden === 1;
    });
    this.assistantConfig.savedPreference$.pipe(takeUntil(this.destroy$)).subscribe((config) => {
      this.assistantInHeader = config.visible === 1 && config.header_hidden === 1;
    });
    this.store.select(state => state.user.userInfo).pipe(
      filter((user): user is User => !!user?.id),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe((user) => {
      this.listenAbly(user.id);
    });
  }

  private checkActiveItem(url: string): void {
    if (url.startsWith('/home')) {
      this.activeItem = 'home';
      this.store.dispatch(PageActions.setTitle({ title: 'Home' }));
    } else if (url.startsWith('/agenda/calendar')) {
      this.activeItem = 'calendar';
      this.store.dispatch(PageActions.setTitle({ title: 'Calendario' }));
    } else {
      this.activeItem = '';
    }
  }

  ngOnDestroy(): void {
    if (this.notifyChannel) {
      this.ablyConnectService.leave(this.notifyChannel);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  goToRoute(route: string): void {
    this.router.navigate([route]);
  }

  get unreadBadge(): string {
    if (this.unreadCount <= 0) {
      return '';
    }
    return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
  }

  onNotificationsScroll(event: Event): void {
    const el = event.target;
    if (!(el instanceof HTMLElement) || this.notificationsLoading || this.notificationsLoadingMore || !this.notificationsHasMore) {
      return;
    }
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceToBottom <= 48) {
      this.loadNotifications(true);
    }
  }

  private loadNotifications(append = false): void {
    const beforeId = append ? this.notifications[this.notifications.length - 1]?.id : undefined;
    if (append) {
      if (this.notificationsLoadingMore || this.notificationsLoading || !this.notificationsHasMore || !beforeId) {
        return;
      }
      this.notificationsLoadingMore = true;
    } else if (this.notificationsLoading) {
      return;
    } else {
      this.notificationsLoading = true;
    }

    this.notificationWalleyService.getList(this.notificationsPageSize, beforeId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        this.unreadCount = data.unread_count;
        if (append) {
          const merged = this.appendNotifications(this.notifications, data.notifications);
          this.notificationsHasMore = merged.length > this.notifications.length && data.has_more;
          this.notifications = merged;
        } else {
          this.notifications = data.notifications;
          this.notificationsHasMore = data.has_more;
        }
        this.notificationsLoading = false;
        this.notificationsLoadingMore = false;
      },
      error: () => {
        this.notificationsLoading = false;
        this.notificationsLoadingMore = false;
      }
    });
  }

  private appendNotifications(
    current: NotificationWalleyItem[],
    incoming: NotificationWalleyItem[]
  ): NotificationWalleyItem[] {
    if (incoming.length === 0) {
      return current;
    }
    const seen = new Set(current.map((item) => item.id));
    const extra = incoming.filter((item) => !seen.has(item.id));
    return extra.length > 0 ? [...current, ...extra] : current;
  }

  private listenAbly(userId: number): void {
    this.notifyChannel = `private:walley-notify.user.${userId}`;
    this.ablyConnectService.listen(this.notifyChannel, 'notification.created', (payload: NotificationWalleyItem & { unread_count?: number }) => {
      if (!payload?.id) {
        return;
      }
      this.notifications = this.notificationWalleyService.applyIncoming(this.notifications, payload);
      if (typeof payload.unread_count === 'number') {
        this.unreadCount = payload.unread_count;
      } else if (!payload.read_at) {
        this.unreadCount += 1;
      }
    });
  }

  openNotification(item: NotificationWalleyItem): void {
    this.notificationWalleyService.markRead(item.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe((unread) => {
      this.unreadCount = unread;
      this.notifications = this.notifications.map((row) =>
        row.id === item.id ? { ...row, read_at: row.read_at ?? new Date().toISOString() } : row
      );
    });
    if (item.route) {
      this.router.navigateByUrl(item.route);
    }
  }

  markAllRead(event: Event): void {
    event.stopPropagation();
    this.notificationWalleyService.markAllRead().pipe(
      takeUntil(this.destroy$)
    ).subscribe((unread) => {
      this.unreadCount = unread;
      this.notifications = this.notifications.map((row) => ({
        ...row,
        read_at: row.read_at ?? new Date().toISOString(),
      }));
    });
  }

  displayCustomerName(customer?: CustomerAutocomplete): string {
    return customer ? customer.denomination! : '';
  }

  goToCustomer(id: number) {
    const targetUrl = `/${this.path}/${id}`;
    const currentUrl = this.router.url;

    if (currentUrl.startsWith(`/${this.path}/`) && currentUrl !== targetUrl) {
      const currentIdMatch = currentUrl.match(/\/(\d+)$/);
      const currentId = currentIdMatch ? parseInt(currentIdMatch[1], 10) : null;

      if (currentId !== id) {
        window.location.href = targetUrl;
        return;
      }
    }

    this.router.navigate([this.path, id]);
  }

  private searchCustomer() {
    const city_field = this.filterForm.get('customer');
    if (city_field) {
      this.filteredCustomers$ = city_field.valueChanges
        .pipe(
          startWith(''),
          map(value => typeof value === 'string' ? value : value?.denomination || ''),
          filter(value => value.length > 1),
          debounceTime(700),
          switchMap((value: string) =>
            value ? this.searchAutocompleteService.getCustomer(0, value) : [])
        );
    }
    else {
      this.filteredCustomers$ = of([]);
    }
  }

  goToProfile() {
    this.router.navigate(['settings/userProfile']);
  }
}
